import { NewCardFields, parseExpiration } from '../../helpers/newCard';
import { onlyNumbers } from '../../utils/currency';

export interface NewCardPayload {
	code?: string;
	encrypted?: string;
	holder?: string;
	first_6?: string;
	last_4?: string;
	exp_month?: string;
	exp_year?: string;
}

export interface TokenizeCustomer {
	name: string;
	email?: string;
	is_foreign?: boolean;
}

type DocumentType = 'CPF' | 'CNPJ';

declare global {
	interface Window {
		Ticto?: {
			init?: (config: { offerCode?: string; containerId?: string }) => void | Promise<void>;
			encryptCard?: (data: {
				cardData: { holder: string; number: string; expMonth: string; expYear: string; securityCode: string };
				cardholder: { identification: { type: DocumentType; number: string }; name: string; email?: string };
				customerData: {
					name: string;
					email?: string;
					cpf: string;
					cnpj: string;
					type: 'person' | 'company';
					is_foreign: boolean;
				};
				offerCode?: string;
			}) => Promise<{ code?: number | string; errors?: Array<{ message: string }> }>;
		};
		Autorizou?: {
			encryptCard?: (
				data: {
					cardData: { holder: string; number: string; expMonth: string; expYear: string; securityCode: string };
					cardHolder: { identification: { type: DocumentType; number: string }; name: string };
				},
				callbacks: {
					onSuccess: (response: { token: string }) => void;
					onError: (error: { errors: unknown }) => void;
				}
			) => Promise<void> | void;
		};
	}
}

export class CardTokenizationError extends Error {
	name = 'CardTokenizationError';
}

export const ICARUS_CONTAINER_ID = 'ticto_tokenize_card';

const SDK_TIMEOUT = 5000;

const loadedScripts = new Map<string, Promise<boolean>>();

export const loadScript = (src: string): Promise<boolean> => {
	if (!loadedScripts.has(src)) {
		loadedScripts.set(
			src,
			new Promise(resolve => {
				const script = document.createElement('script');
				script.src = src;
				script.async = true;
				script.onload = () => resolve(true);
				script.onerror = () => resolve(false);
				document.head.appendChild(script);
			})
		);
	}

	return loadedScripts.get(src);
};

const waitFor = (isReady: () => boolean, timeout: number): Promise<boolean> =>
	new Promise(resolve => {
		if (isReady()) {
			resolve(true);
			return;
		}

		const startedAt = Date.now();
		const interval = setInterval(() => {
			if (isReady()) {
				clearInterval(interval);
				resolve(true);
			} else if (Date.now() - startedAt > timeout) {
				clearInterval(interval);
				resolve(false);
			}
		}, 100);
	});

export const isIcarusEnabled = (): boolean =>
	process.env.REACT_APP_ICARUS_ENABLED === 'true' && Boolean(process.env.REACT_APP_ICARUS_URL);

const icarusScriptUrl = () => `${String(process.env.REACT_APP_ICARUS_URL).replace(/\/$/, '')}/embed/tokenize.js`;

const autorizouScriptUrl = () =>
	`https://keranos.autorizou.${process.env.REACT_APP_MIDAS_ENVIRONMENT === 'production' ? 'app' : 'dev'}/embed/gateway.js`;

const documentType = (document: string): DocumentType => (document.length === 11 ? 'CPF' : 'CNPJ');

const tokenizeWithIcarus = async (
	fields: NewCardFields,
	customer: TokenizeCustomer,
	offerCode: string
): Promise<NewCardPayload | null> => {
	if (!window.Ticto && !(await loadScript(icarusScriptUrl()))) return null;

	if (window.Ticto?.init) {
		await Promise.resolve(window.Ticto.init({ offerCode, containerId: ICARUS_CONTAINER_ID })).catch(() => undefined);
	}

	if (!(await waitFor(() => Boolean(window.Ticto?.encryptCard), SDK_TIMEOUT))) return null;

	const document = onlyNumbers(fields.document);
	const expiration = parseExpiration(fields.expiration);
	const result = await window.Ticto.encryptCard({
		cardData: {
			holder: fields.holder.trim(),
			number: onlyNumbers(fields.number),
			expMonth: expiration.month,
			expYear: expiration.year,
			securityCode: onlyNumbers(fields.securityCode),
		},
		cardholder: {
			identification: { type: documentType(document), number: document },
			name: fields.holder.trim(),
			email: customer.email,
		},
		customerData: {
			name: customer.name,
			email: customer.email,
			cpf: document.length === 11 ? document : '',
			cnpj: document.length === 14 ? document : '',
			type: document.length === 11 ? 'person' : 'company',
			is_foreign: Boolean(customer.is_foreign),
		},
		offerCode,
	});

	if (result?.errors?.length) {
		throw new CardTokenizationError(result.errors[0].message);
	}

	return result?.code ? { code: String(result.code) } : null;
};

const tokenizeWithAutorizou = async (fields: NewCardFields): Promise<NewCardPayload | null> => {
	if (!window.Autorizou && !(await loadScript(autorizouScriptUrl()))) return null;

	if (!(await waitFor(() => Boolean(window.Autorizou?.encryptCard), SDK_TIMEOUT))) return null;

	const number = onlyNumbers(fields.number);
	const document = onlyNumbers(fields.document);
	const expiration = parseExpiration(fields.expiration);

	const token = await new Promise<string | null>(resolve => {
		Promise.resolve(
			window.Autorizou.encryptCard(
				{
					cardData: {
						holder: fields.holder.trim(),
						number,
						expMonth: expiration.month,
						expYear: expiration.year.slice(-2),
						securityCode: onlyNumbers(fields.securityCode),
					},
					cardHolder: {
						identification: { type: documentType(document), number: document },
						name: fields.holder.trim(),
					},
				},
				{
					onSuccess: response => resolve(response?.token ?? null),
					onError: () => resolve(null),
				}
			)
		).catch(() => resolve(null));
	});

	if (!token) return null;

	return {
		encrypted: token,
		holder: fields.holder.trim(),
		first_6: number.slice(0, 6),
		last_4: number.slice(-4),
		exp_month: expiration.month,
		exp_year: expiration.year,
	};
};

export const tokenizeCard = async (
	fields: NewCardFields,
	customer: TokenizeCustomer,
	offerCode: string
): Promise<NewCardPayload> => {
	if (isIcarusEnabled()) {
		const icarusCard = await tokenizeWithIcarus(fields, customer, offerCode).catch(error => {
			if (error instanceof CardTokenizationError) throw error;
			return null;
		});
		if (icarusCard) return icarusCard;
	}

	const autorizouCard = await tokenizeWithAutorizou(fields);
	if (autorizouCard) return autorizouCard;

	throw new CardTokenizationError();
};
